import type { SubscriptionPlan } from '../types';

export type SubscriptionActionAvailability = {
  canCancel: boolean;
  canPause: boolean;
  canReactivate: boolean;
  canShipNow: boolean;
  canChangeDate: boolean;
};

const createEmptyActionAvailability = (): SubscriptionActionAvailability => ({
  canCancel: false,
  canPause: false,
  canReactivate: false,
  canShipNow: false,
  canChangeDate: false,
});

export const getSubscriptionStatusDisplayValue = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'Active':
    case 'ACTIVE':
      return 'Active';
    case 'paused':
    case 'Paused':
    case 'PAUSED':
      return 'Paused';
    case 'cancelled':
    case 'Cancelled':
    case 'CANCELLED':
      return 'Cancelled';
    case 'inactive':
    case 'Inactive':
    case 'INACTIVE':
      return 'No Subscription';
    default:
      return status || '-';
  }
};

export const getSubscriptionStatusTooltip = (status: string) => {
  const normalized = status?.toLowerCase();
  if (!normalized) return '';

  if (normalized === 'active') {
    return 'The subscription is active and orders will be processed according to schedule.';
  }
  if (normalized === 'paused') {
    return 'The subscription is temporarily paused. No orders will be processed until reactivated.';
  }
  if (normalized === 'cancelled') {
    return 'The subscription has been cancelled. No further orders will be processed.';
  }
  if (normalized === 'inactive') {
    return 'The customer does not have any active subscriptions.';
  }

  return '';
};

export const getActionAvailability = (
  subscription: SubscriptionPlan | null | undefined,
): SubscriptionActionAvailability => {
  if (!subscription) {
    return createEmptyActionAvailability();
  }
  if (subscription.isLocked) {
    return createEmptyActionAvailability();
  }

  const canCancel = subscription.status?.toLowerCase() === 'active';
  const canPause = subscription.status?.toLowerCase() === 'active';
  const canReactivate = ['paused', 'cancelled'].includes(subscription.status?.toLowerCase());
  const canShipNow =
    subscription.status?.toLowerCase() === 'active' && !subscription.isOrderInProgress;
  const canChangeDate =
    subscription.status?.toLowerCase() === 'active' && !subscription.isOrderInProgress;

  return { canCancel, canPause, canReactivate, canShipNow, canChangeDate };
};
